import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const fmt = (v) => v === null || v === undefined ? '-' : new Intl.NumberFormat('it-IT').format(v);
const fmtPct = (v) => v === null || v === undefined ? '-' : v.toFixed(2) + '%';

export default function ScrapKPIPage() {
  useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/indicator-scrap', { params: { year } });
      setData(res.data);
    } catch { toast.error('Errore caricamento dati'); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    let produced = 0, ncQty = 0, concession = 0, scrapped = 0, reworked = 0;
    data.forEach(m => { produced += m.produced; ncQty += m.nc_qty; concession += m.concession; scrapped += m.scrapped; reworked += m.reworked; });
    const scrapPct = produced > 0 ? (scrapped / produced) * 100 : 0;
    return { produced, ncQty, concession, scrapped, reworked, scrapPct };
  }, [data]);

  const chartStacked = useMemo(() => data.map(m => ({
    name: MONTHS[m.month - 1],
    Concessione: m.concession || null,
    Scartati: m.scrapped || null,
    Rilavorati: m.reworked || null,
  })), [data]);

  const chartLine = useMemo(() => data.map(m => ({
    name: MONTHS[m.month - 1],
    '% Scarto': m.scrap_pct > 0 ? m.scrap_pct : null,
  })), [data]);

  return (
    <div className="space-y-5" data-testid="scrap-kpi-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="scrap-title">IND004 – Scarti interni</h1>
          <p className="text-sm text-slate-500 mt-0.5">Analisi automatica da NC interne e produzione</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
          <span className="text-sm font-bold text-slate-900 min-w-[4rem] text-center" data-testid="year-display">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="summary-kpis">
        <KpiCard label="Pezzi Prodotti" value={fmt(summary.produced)} testId="kpi-produced" />
        <KpiCard label="Pezzi NC Interni" value={fmt(summary.ncQty)} testId="kpi-nc" accent={summary.ncQty > 0 ? 'text-red-600' : 'text-emerald-600'} />
        <KpiCard label="In Concessione" value={fmt(summary.concession)} testId="kpi-concession" />
        <KpiCard label="Scartati" value={fmt(summary.scrapped)} testId="kpi-scrapped" accent={summary.scrapped > 0 ? 'text-red-600' : 'text-emerald-600'} />
        <KpiCard label="% Scarto" value={fmtPct(summary.scrapPct)} testId="kpi-pct" accent={summary.scrapPct > 5 ? 'text-red-600' : summary.scrapPct > 2 ? 'text-amber-600' : 'text-emerald-600'} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="scrap-stacked-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Composizione NC Interne / Mese</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartStacked} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Legend wrapperStyle={{ fontSize: '13px' }} />
              <Bar dataKey="Concessione" stackId="a" fill="#f59e0b" />
              <Bar dataKey="Scartati" stackId="a" fill="#ef4444" />
              <Bar dataKey="Rilavorati" stackId="a" fill="#64748b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="scrap-line-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">% Scarto Mensile</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartLine} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v) => [`${v}%`]} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Line type="monotone" dataKey="% Scarto" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4, fill: '#ef4444' }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="scrap-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-3 py-2.5 w-28">Mese</th>
                <th className="px-3 py-2.5 text-right w-24">Prodotti</th>
                <th className="px-3 py-2.5 text-right w-24">NC Interni</th>
                <th className="px-3 py-2.5 text-right w-24">Concessione</th>
                <th className="px-3 py-2.5 text-right w-24">Scartati</th>
                <th className="px-3 py-2.5 text-right w-24">Rilavorati</th>
                <th className="px-3 py-2.5 text-right w-20">% Scarto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(m => {
                const hasNc = m.nc_qty > 0;
                return (
                  <React.Fragment key={m.month}>
                    <tr
                      className={`transition-colors ${hasNc ? 'cursor-pointer hover:bg-slate-50' : ''} ${expandedMonth === m.month ? 'bg-slate-50' : ''}`}
                      onClick={() => hasNc && setExpandedMonth(expandedMonth === m.month ? null : m.month)}
                      data-testid={`row-${m.month}`}
                    >
                      <td className="px-3 py-2 text-slate-400">{hasNc && (expandedMonth === m.month ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</td>
                      <td className="px-3 py-2 font-medium text-slate-700">{MONTH_FULL[m.month - 1]}</td>
                      <td className="px-3 py-2 text-right font-mono">{m.produced > 0 ? fmt(m.produced) : '-'}</td>
                      <td className="px-3 py-2 text-right"><span className={`font-mono font-semibold ${m.nc_qty > 0 ? 'text-red-600' : 'text-slate-400'}`}>{m.nc_qty > 0 ? fmt(m.nc_qty) : '-'}</span></td>
                      <td className="px-3 py-2 text-right font-mono text-amber-600">{m.concession > 0 ? fmt(m.concession) : '-'}</td>
                      <td className="px-3 py-2 text-right"><span className={`font-mono font-semibold ${m.scrapped > 0 ? 'text-red-600' : 'text-slate-400'}`}>{m.scrapped > 0 ? fmt(m.scrapped) : '-'}</span></td>
                      <td className="px-3 py-2 text-right font-mono">{m.reworked > 0 ? fmt(m.reworked) : '-'}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-mono font-semibold ${m.scrap_pct > 5 ? 'text-red-600' : m.scrap_pct > 2 ? 'text-amber-600' : m.scrap_pct > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{m.scrap_pct > 0 ? fmtPct(m.scrap_pct) : '-'}</span>
                      </td>
                    </tr>
                    {expandedMonth === m.month && m.ncs && m.ncs.length > 0 && (
                      <tr><td colSpan={8} className="px-0 py-0">
                        <div className="bg-slate-50 border-t border-b border-slate-200 px-8 py-3" data-testid={`detail-${m.month}`}>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-slate-500 uppercase tracking-wide">
                                <th className="pb-1.5 pr-3">NC</th>
                                <th className="pb-1.5 pr-3">Prodotto</th>
                                <th className="pb-1.5 pr-3">Lotto</th>
                                <th className="pb-1.5 pr-3 text-right">Qty NC</th>
                                <th className="pb-1.5 pr-3 text-right">Concessione</th>
                                <th className="pb-1.5 pr-3 text-right">Scartati</th>
                                <th className="pb-1.5 pr-3 text-right">Rilavorati</th>
                                <th className="pb-1.5">Stato</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {m.ncs.map(nc => (
                                <tr key={nc.id} className="text-slate-700">
                                  <td className="py-1.5 pr-3 font-mono font-semibold">{nc.nc_number}</td>
                                  <td className="py-1.5 pr-3">{nc.product_code} - {nc.product_name}</td>
                                  <td className="py-1.5 pr-3 font-mono">{nc.lot_code}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono">{nc.total_nonconforming_quantity}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono text-amber-600">{nc.concession_quantity || '-'}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono text-red-600">{nc.scrapped_quantity || '-'}</td>
                                  <td className="py-1.5 pr-3 text-right font-mono">{nc.reworked_quantity || '-'}</td>
                                  <td className="py-1.5"><StatusBadge status={nc.status} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-semibold text-sm border-t-2 border-slate-300">
                <td className="px-3 py-2.5"></td>
                <td className="px-3 py-2.5 text-slate-900">Totale</td>
                <td className="px-3 py-2.5 text-right font-mono" data-testid="foot-produced">{fmt(summary.produced)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-red-600" data-testid="foot-nc">{fmt(summary.ncQty)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-amber-600">{fmt(summary.concession)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-red-600">{fmt(summary.scrapped)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{fmt(summary.reworked)}</td>
                <td className="px-3 py-2.5 text-right font-mono" data-testid="foot-pct">
                  <span className={summary.scrapPct > 5 ? 'text-red-600' : summary.scrapPct > 2 ? 'text-amber-600' : 'text-emerald-600'}>{fmtPct(summary.scrapPct)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, unit, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
      {unit && <p className="text-xs text-slate-400 mt-0.5">{unit}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { open: 'bg-red-100 text-red-700', in_progress: 'bg-amber-100 text-amber-700', closed: 'bg-emerald-100 text-emerald-700' };
  const labels = { open: 'Aperta', in_progress: 'In corso', closed: 'Chiusa' };
  return <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{labels[status] || status}</span>;
}
